import React, { useEffect, useState, useMemo, useRef } from 'react'
import BasicDrawer from '../../../components/Drawer'
import { useForm, Controller } from 'react-hook-form'
import { Col, ConfigProvider, Row, Select, Spin, Upload, Tag } from 'antd'
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons'
import LabelWithSelect from '../../../components/LabelWithSelect'
import { Button, TextField } from '../../../components'
import { ApiCaller, Colors, Utils } from '../../../config'
import { useMutation } from 'react-query'
import BasicText from '../../../components/Text/BasicText'
import { yupResolver } from '@hookform/resolvers/yup'
import { AssignIncidentSchema, CreateIncidentSchema } from '../../../config/Schema'
import { useDispatch, useSelector } from 'react-redux'
import { ManageIncidentsAction } from '../../../store/actions'
import { useQuery } from 'react-query'
import { getAllProperties, getAllUsers } from '../../../config/getApis'
import useColors from '../../../hooks/useColors'

const AssignIncidentDrawer = ({
    drawerStates,
    setDrawerStates,
    onClose,
    prefillIncident,
    onPrefillHandled,
    onSuccess,
}) => {
    const { colorPrimary } = useColors()
    const dispatch = useDispatch()
    const { assignIncidentDetail, incidentDetailData } = useSelector(
        (state) => state?.ManageIncidentReducer,
    )

    // Determine mode: prefill = assign-only, no prefill = create then assign
    const isPrefillMode = Boolean(prefillIncident)

    // incident options (units) from property+incidentType combo
    const incidentOptions = useMemo(() => {
        return (assignIncidentDetail?.data || []).map((incident) => ({
            label: `${incident.name || '-'} - (${incident.floor} ${incident.unit})`,
            value: incident?.id,
        }))
    }, [assignIncidentDetail])

    // users + properties for selects
    const roles = { guard: true, supervisor: true }
    // NOTE: a dedicated query key. Sharing the plain 'users' key with the other
    // screens made this drawer render whichever list react-query had cached first,
    // which is fetched with different role filters.
    const { data: usersOptions, isLoading: usersLoading } = useQuery(
        ['users', 'assign-incident', roles],
        () => getAllUsers(roles),
        { enabled: drawerStates.assignIncidentDrawer },
    )
    const { data: propertiesRaw } = useQuery('properties', getAllProperties, {
        enabled: drawerStates.assignIncidentDrawer,
    })

    // The properties query only starts when the drawer opens, so on the first open
    // the options are still empty and the Select would fall back to printing the raw
    // id. Seeding the incident's own property/type keeps the name visible right away.
    const propertiesOptions = useMemo(() => {
        const options = (propertiesRaw || []).map((p) => ({
            label: p.name,
            value: p.id,
        }))
        const preProperty = prefillIncident?.property
        if (
            preProperty?.id &&
            !options.some((option) => option.value === preProperty.id)
        ) {
            return [
                { label: preProperty.name || '-', value: preProperty.id },
                ...options,
            ]
        }
        return options
    }, [propertiesRaw, prefillIncident])

    // cascade state
    const [propertyIncidentOptions, setPropertyIncidentOptions] = useState([])

    // same first-open problem as the property list above: the types are still being
    // fetched, so seed the prefilled one to avoid showing its id
    const incidentTypeOptions = useMemo(() => {
        const preType = prefillIncident?.incidentType
        if (
            preType?.id &&
            !propertyIncidentOptions.some(
                (option) => option.value === preType.id,
            )
        ) {
            return [
                { label: preType.name || '-', value: preType.id },
                ...propertyIncidentOptions,
            ]
        }
        return propertyIncidentOptions
    }, [propertyIncidentOptions, prefillIncident])

    const [addressOptions, setAddressOptions] = useState([])
    const [addressLoading, setAddressLoading] = useState(false)
    const [locationOptions, setLocationOptions] = useState([])
    const [locationLoading, setLocationLoading] = useState(false)
    const [selectedAddress, setSelectedAddress] = useState('')
    const [selectedLocation, setSelectedLocation] = useState('')
    const skipNextCascadeRef = useRef(false)

    // read-only display values (prefill mode)
    const [incidents, setIncidents] = useState({
        problemUnit: '',
        problemAddress: '',
        problemLocation: '',
    })

    // create-mode extra fields
    const [floor, setFloor] = useState('')
    const [unit, setUnit] = useState('')

    // attachment upload state
    const [attachments, setAttachments] = useState([]) // [{ uid, name, mediaId, uploading }]
    const [isSubmitting, setIsSubmitting] = useState(false)

    // top-level cascade selects
    const [fieldValues, setFieldValues] = useState({
        propertyReportingThisIssue: '',
        issueType: '',
        incidentType: '',
    })

    // ─── helpers ───────────────────────────────────────────────────────────────
    const fetchPropertyIncidentTypes = async (propertyId) => {
        if (!propertyId) { setPropertyIncidentOptions([]); return }
        try {
            const res = await ApiCaller.Get(`/property/${propertyId}/incident-type`)
            setPropertyIncidentOptions(
                (res?.data?.data || []).map((item) => ({ label: item.name, value: item.id })),
            )
        } catch { setPropertyIncidentOptions([]) }
    }

    const fetchPropertyAddresses = async (propertyId) => {
        if (!propertyId) { setAddressOptions([]); return }
        setAddressLoading(true)
        try {
            const res = await ApiCaller.Get(`/property/${propertyId}/addresses`)
            const data = res?.data?.data?.addresses || []
            setAddressOptions(
                data.map((item) => ({
                    label: [item.streetNumber, item.streetName, item.city, item.zip]
                        .filter(Boolean)
                        .join(' - '),
                    value: item.id,
                })),
            )
        } catch { setAddressOptions([]) }
        finally { setAddressLoading(false) }
    }

    const fetchPropertyLocations = async (propertyId) => {
        if (!propertyId) { setLocationOptions([]); return }
        setLocationLoading(true)
        try {
            const res = await ApiCaller.Get(`/property/${propertyId}`)
            setLocationOptions(
                (res?.data?.data?.locations || []).map((item) => ({
                    label: item.name,
                    value: item.id,
                })),
            )
        } catch { setLocationOptions([]) }
        finally { setLocationLoading(false) }
    }

    // ─── upload single file, returns mediaId ────────────────────────────────────
    const uploadFile = async (file) => {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('type', 'incident')
        const res = await ApiCaller.Post('/media/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
        return res?.data?.data?.id
    }

    // ─── react-hook-form ────────────────────────────────────────────────────────
    const defaultValues = { assignId: '', level: '', notes: '' }
    const { handleSubmit, control, reset } = useForm({
        resolver: yupResolver(isPrefillMode ? AssignIncidentSchema : CreateIncidentSchema),
        mode: 'onChange',
        defaultValues,
    })

    // ─── effects ────────────────────────────────────────────────────────────────
    // fill read-only address/unit/location from fetched incident detail (prefill mode)
    useEffect(() => {
        if (incidentDetailData && Object.keys(incidentDetailData).length > 0) {
            const addr = incidentDetailData?.address
            setIncidents({
                problemAddress: addr
                    ? [addr.state, addr.city, addr.zip].filter(Boolean).join(' - ')
                    : '',
                problemUnit: incidentDetailData?.unit || '',
                problemLocation: addr
                    ? [addr.streetNumber, addr.streetName, addr.buildingNumber]
                        .filter(Boolean)
                        .join(', ')
                    : '',
            })
            if (addr?.id) setSelectedAddress(addr.id)
            if (incidentDetailData?.location?.id)
                setSelectedLocation(incidentDetailData.location.id)
        }
    }, [incidentDetailData])

    // property change → load incident types + addresses + locations
    const propertyChangeHandler = (value) => {
        const id = value ?? ''
        setFieldValues((p) => ({ ...p, propertyReportingThisIssue: id, issueType: '', incidentType: '' }))
        setSelectedAddress('')
        setSelectedLocation('')
        setIncidents({ problemUnit: '', problemAddress: '', problemLocation: '' })
        fetchPropertyIncidentTypes(id)
        if (id) {
            fetchPropertyAddresses(id)
            fetchPropertyLocations(id)
        } else {
            setAddressOptions([])
            setLocationOptions([])
        }
    }

    // incident type change
    const issueTypeChangeHandler = (value) => {
        setFieldValues((p) => ({ ...p, issueType: value ?? '', incidentType: '' }))
    }

    // property + incidentType → GetAssignIncidentDetail (units list)
    useEffect(() => {
        if (skipNextCascadeRef.current) { skipNextCascadeRef.current = false; return }
        if (fieldValues.propertyReportingThisIssue && fieldValues.issueType) {
            dispatch(
                ManageIncidentsAction.GetAssignIncidentDetail({
                    propertyReportingThisIssue: fieldValues.propertyReportingThisIssue,
                    issueType: fieldValues.issueType,
                }),
            )
        } else {
            dispatch(ManageIncidentsAction.GetAssignIncidentDetailCleaner())
        }
    }, [fieldValues.propertyReportingThisIssue, fieldValues.issueType])

    // auto-select unit if only one match
    useEffect(() => {
        if (!(fieldValues.propertyReportingThisIssue && fieldValues.issueType)) return
        const matches = incidentOptions || []
        if (matches.length === 1) {
            const only = matches[0].value
            setFieldValues((p) => (p.incidentType === only ? p : { ...p, incidentType: only }))
        } else {
            setFieldValues((p) => (p.incidentType === '' ? p : { ...p, incidentType: '' }))
        }
    }, [incidentOptions])

    // specific incident selected → fetch full detail
    useEffect(() => {
        if (fieldValues.incidentType) {
            dispatch(ManageIncidentsAction.GetManageIncidentDetail(fieldValues.incidentType))
        } else {
            setIncidents({ problemAddress: '', problemUnit: '', problemLocation: '' })
        }
    }, [fieldValues.incidentType])

    // prefill from table Assign button
    useEffect(() => {
        if (prefillIncident) {
            skipNextCascadeRef.current = true
            const propertyId = prefillIncident?.property?.id ?? ''
            const issueTypeId = prefillIncident?.incidentType?.id ?? ''
            const incidentId = prefillIncident?.id ?? ''
            setFieldValues({ propertyReportingThisIssue: propertyId, issueType: issueTypeId, incidentType: incidentId })
            fetchPropertyIncidentTypes(propertyId)
            if (propertyId) { fetchPropertyAddresses(propertyId); fetchPropertyLocations(propertyId) }
            if (incidentId) dispatch(ManageIncidentsAction.GetManageIncidentDetail(incidentId))
        }
    }, [prefillIncident])

    // ─── submission ─────────────────────────────────────────────────────────────
    const handleSave = async (formData) => {
        setIsSubmitting(true)
        try {
            if (isPrefillMode) {
                // ── ASSIGN ONLY (PATCH) ──
                const id = Number(fieldValues.incidentType)
                const res = await ApiCaller.Patch(`/incident/${id}/assign`, formData)
                Utils.showMessage('success', res?.data?.message || 'Incident assigned successfully')
            } else {
                // ── CREATE (POST) ──
                if (!selectedAddress) {
                    Utils.showMessage('error', 'Please select an address')
                    return
                }
                if (!fieldValues.issueType) {
                    Utils.showMessage('error', 'Please select an incident type')
                    return
                }

                // Upload pending attachments that haven't been uploaded yet
                const mediaIds = await Promise.all(
                    attachments.map(async (att) => {
                        if (att.mediaId) return att.mediaId
                        return await uploadFile(att.originFileObj)
                    }),
                )

                const createPayload = {
                    addressId: Number(selectedAddress),
                    incidentTypeId: Number(fieldValues.issueType),
                    incidentAttachmentsId: mediaIds.filter(Boolean),
                    floor: floor || undefined,
                    unit: unit || undefined,
                }

                const createRes = await ApiCaller.Post('/incident', createPayload)
                const newIncidentId = createRes?.data?.data?.id

                // After successful create, call PATCH to assign via callback if assignId provided
                if (formData.assignId && newIncidentId) {
                    await ApiCaller.Patch(`/incident/${newIncidentId}/assign`, formData)
                    Utils.showMessage('success', 'Incident created and assigned successfully')
                } else {
                    Utils.showMessage('success', createRes?.data?.message || 'Incident created successfully')
                }
            }

            // ── close + refresh table ──
            setDrawerStates((prev) => ({ ...prev, assignIncidentDrawer: false }))
            reset(defaultValues)
            onSuccess && onSuccess()
        } catch (error) {
            Utils.showMessage('error', error?.response?.data?.message || 'Something went wrong')
        } finally {
            setIsSubmitting(false)
        }
    }

    // ─── reset after close ───────────────────────────────────────────────────────
    const afterCloseHandler = () => {
        if (!drawerStates?.assignIncidentDrawer) {
            reset(defaultValues)
            setIncidents({ problemAddress: '', problemUnit: '', problemLocation: '' })
            setFieldValues({ propertyReportingThisIssue: '', issueType: '', incidentType: '' })
            setSelectedAddress('')
            setSelectedLocation('')
            setPropertyIncidentOptions([])
            setAddressOptions([])
            setLocationOptions([])
            setFloor('')
            setUnit('')
            setAttachments([])
            skipNextCascadeRef.current = false
            dispatch(ManageIncidentsAction.GetAssignIncidentDetailCleaner())
            onPrefillHandled && onPrefillHandled()
        }
    }

    // ─── attachment handlers ─────────────────────────────────────────────────────
    const handleAttachmentChange = ({ fileList }) => {
        setAttachments(fileList)
    }

    const selectTheme = {
        components: {
            Select: {
                optionSelectedBg: colorPrimary,
                optionActiveBg: Colors.LightGray,
                optionSelectedColor: Colors.White,
                selectorBg: Colors.LightGray,
            },
        },
    }

    // ─── render ─────────────────────────────────────────────────────────────────
    return (
        <>
            <BasicDrawer
                openDrawer={drawerStates?.assignIncidentDrawer}
                onClose={onClose}
                afterOpenChange={afterCloseHandler}
                width={600}
                title={isPrefillMode ? 'Assign Incident/Issues' : 'Create & Assign Incident'}
                children={
                    <>
                        {/* ── Row 1: Property + Incident Type ── */}
                        <Row justify={'space-between'} className='mb-3' gutter={[8, 8]}>
                            <Col lg={14} md={14} sm={24} xs={24}>
                                <label className='font-normal' style={{ color: Colors.LabelColor }}>
                                    Property Reporting This Issue
                                </label>
                                <ConfigProvider theme={selectTheme}>
                                    <Select
                                        className='w-full h-9 outline-none rounded'
                                        value={fieldValues?.propertyReportingThisIssue || undefined}
                                        onChange={(value) => propertyChangeHandler(value)}
                                        options={propertiesOptions}
                                        placeholder='Select Property'
                                        allowClear
                                        onClear={() => propertyChangeHandler('')}
                                    />
                                </ConfigProvider>
                            </Col>
                            <Col lg={9} md={9} sm={24} xs={24}>
                                <label className='font-normal' style={{ color: Colors.LabelColor }}>
                                    Incident Type
                                </label>
                                <ConfigProvider theme={selectTheme}>
                                    <Select
                                        className='w-full h-9 outline-none rounded'
                                        value={fieldValues?.issueType || undefined}
                                        disabled={!fieldValues.propertyReportingThisIssue}
                                        onChange={(value) => issueTypeChangeHandler(value)}
                                        options={incidentTypeOptions}
                                        placeholder='Select Type'
                                        allowClear
                                        onClear={() => issueTypeChangeHandler('')}
                                    />
                                </ConfigProvider>
                            </Col>
                        </Row>

                        {/* ── Unit select (only when multiple incidents returned) ── */}
                        {isPrefillMode && incidentOptions && incidentOptions.length > 1 && (
                            <Row className='mb-3'>
                                <Col span={24}>
                                    <label className='font-normal' style={{ color: Colors.LabelColor }}>
                                        Select Unit
                                    </label>
                                    <ConfigProvider theme={selectTheme}>
                                        <Select
                                            className='w-full h-9 outline-none rounded'
                                            value={fieldValues?.incidentType || undefined}
                                            onChange={(value) =>
                                                setFieldValues((p) => ({ ...p, incidentType: value }))
                                            }
                                            options={incidentOptions}
                                            placeholder='Select Unit'
                                        />
                                    </ConfigProvider>
                                </Col>
                            </Row>
                        )}

                        {/* ── Address ── */}
                        <Row gutter={[16, 8]} className='mb-3'>
                            <Col lg={13} md={13} sm={24} xs={24}>
                                <label className='font-normal' style={{ color: Colors.LabelColor }}>
                                    Problem Address
                                </label>
                                {isPrefillMode ? (
                                    <BasicText
                                        text={incidents.problemAddress || '-'}
                                        className='overflow-y-auto overflow-x-hidden w-full px-2 rounded h-9 flex items-center'
                                        style={{ backgroundColor: '#EFF2F5', paddingTop: 5, paddingBottom: 5 }}
                                    />
                                ) : (
                                    <ConfigProvider theme={selectTheme}>
                                        <Select
                                            className='w-full h-9 outline-none rounded'
                                            value={selectedAddress || undefined}
                                            disabled={
                                                !fieldValues.propertyReportingThisIssue ||
                                                !fieldValues.issueType ||
                                                addressLoading
                                            }
                                            onChange={(value) => {
                                                setSelectedAddress(value ?? '')
                                                setSelectedLocation('')
                                            }}
                                            options={addressOptions}
                                            placeholder={
                                                addressLoading
                                                    ? 'Loading...'
                                                    : !fieldValues.issueType
                                                        ? 'Select Incident Type first'
                                                        : 'Select Address'
                                            }
                                            allowClear
                                            onClear={() => { setSelectedAddress(''); setSelectedLocation('') }}
                                            notFoundContent={
                                                addressLoading ? <Spin size='small' /> : 'No addresses found'
                                            }
                                        />
                                    </ConfigProvider>
                                )}
                            </Col>

                            <Col lg={11} md={11} sm={24} xs={24}>
                                <label className='font-normal' style={{ color: Colors.LabelColor }}>
                                    Problem Unit
                                </label>
                                <BasicText
                                    text={incidents.problemUnit || '-'}
                                    className='overflow-y-auto overflow-x-hidden w-full px-2 rounded h-9 flex items-center'
                                    style={{ backgroundColor: '#EFF2F5', paddingTop: 5, paddingBottom: 5 }}
                                />
                            </Col>
                        </Row>

                        {/* ── Location ── */}
                        <Row className='mb-3'>
                            <Col span={24}>
                                <label className='font-normal' style={{ color: Colors.LabelColor }}>
                                    Location
                                </label>
                                {isPrefillMode ? (
                                    <BasicText
                                        text={incidents.problemLocation || '-'}
                                        className='overflow-y-auto overflow-x-hidden w-full px-2 rounded h-9 flex items-center'
                                        style={{ backgroundColor: '#EFF2F5' }}
                                    />
                                ) : (
                                    <ConfigProvider theme={selectTheme}>
                                        <Select
                                            className='w-full h-9 outline-none rounded'
                                            value={selectedLocation || undefined}
                                            disabled={!selectedAddress || locationLoading}
                                            onChange={(value) => setSelectedLocation(value ?? '')}
                                            options={locationOptions}
                                            placeholder={
                                                locationLoading
                                                    ? 'Loading...'
                                                    : !selectedAddress
                                                        ? 'Select Address first'
                                                        : 'Select Location'
                                            }
                                            allowClear
                                            onClear={() => setSelectedLocation('')}
                                            notFoundContent={
                                                locationLoading ? <Spin size='small' /> : 'No locations found'
                                            }
                                        />
                                    </ConfigProvider>
                                )}
                            </Col>
                        </Row>

                        {/* ── CREATE MODE ONLY: Floor + Unit inputs + Attachments ── */}
                        {!isPrefillMode && (
                            <>
                                <Row gutter={[16, 8]} className='mb-3'>
                                    <Col lg={12} md={12} sm={24} xs={24}>
                                        <label className='font-normal' style={{ color: Colors.LabelColor }}>
                                            Floor
                                        </label>
                                        <input
                                            value={floor}
                                            onChange={(e) => setFloor(e.target.value)}
                                            placeholder='e.g. 8th'
                                            className='w-full h-9 px-3 rounded outline-none'
                                            style={{ backgroundColor: '#EFF2F5', border: 'none', fontSize: 14 }}
                                        />
                                    </Col>
                                    <Col lg={12} md={12} sm={24} xs={24}>
                                        <label className='font-normal' style={{ color: Colors.LabelColor }}>
                                            Unit
                                        </label>
                                        <input
                                            value={unit}
                                            onChange={(e) => setUnit(e.target.value)}
                                            placeholder='e.g. Five'
                                            className='w-full h-9 px-3 rounded outline-none'
                                            style={{ backgroundColor: '#EFF2F5', border: 'none', fontSize: 14 }}
                                        />
                                    </Col>
                                </Row>

                                {/* <Row className='mb-3'>
                                    <Col span={24}>
                                        <label className='font-normal' style={{ color: Colors.LabelColor }}>
                                            Attachments (optional)
                                        </label>
                                        <Upload
                                            multiple
                                            beforeUpload={() => false}
                                            fileList={attachments}
                                            onChange={handleAttachmentChange}
                                            accept='image/*,application/pdf'
                                            listType='picture'
                                        >
                                            <button
                                                type='button'
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 6,
                                                    padding: '6px 16px',
                                                    background: '#EFF2F5',
                                                    border: `1px dashed ${colorPrimary}`,
                                                    borderRadius: 6,
                                                    cursor: 'pointer',
                                                    color: colorPrimary,
                                                    fontSize: 13,
                                                    marginTop: 4,
                                                }}
                                            >
                                                <UploadOutlined /> Upload Files
                                            </button>
                                        </Upload>
                                    </Col>
                                </Row> */}
                            </>
                        )}

                        {/* ── Common form: Assign To + Level + Notes ── */}
                        <form>
                            <Row gutter={[16, 16]} className='mb-3'>
                                <Col lg={13} md={13} sm={24} xs={24}>
                                    <Controller
                                        name='assignId'
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <>
                                                {/* `items` switches LabelWithSelect over to
                                                    PaginatedSelect: a search box on top and the
                                                    list revealed in chunks while scrolling, so the
                                                    full user list stays reachable. */}
                                                <LabelWithSelect
                                                    items={usersOptions || []}
                                                    loading={usersLoading}
                                                    allowClear
                                                    placeholder={'Search user'}
                                                    labeltext={'Assign This Issue To'}
                                                    field={field}
                                                    labelClass={'font-normal'}
                                                    inputClass={'outline-none py-1 rounded h-9'}
                                                />
                                                {fieldState?.error && (
                                                    <p style={{ color: Colors.Error }}>
                                                        {fieldState.error.message}
                                                    </p>
                                                )}
                                            </>
                                        )}
                                    />
                                </Col>

                                <Col lg={11} md={11} sm={12} xs={12}>
                                    <Controller
                                        name='level'
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <div>
                                                <LabelWithSelect
                                                    options={[
                                                        { value: '3', label: 'High' },
                                                        { value: '2', label: 'Medium' },
                                                        { value: '1', label: 'Low' },
                                                    ]}
                                                    labelColor={Colors.Error}
                                                    labeltext={'Level'}
                                                    field={field}
                                                    labelClass={'font-normal'}
                                                    inputClass={'outline-none py-1 rounded h-9'}
                                                />
                                                {fieldState?.error && (
                                                    <p style={{ color: Colors.Error }}>
                                                        {fieldState.error.message}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    />
                                </Col>
                            </Row>

                            <Row gutter={[16, 16]} className='mb-2'>
                                <Col span={24}>
                                    <Controller
                                        name='notes'
                                        control={control}
                                        render={({ field: { value, onChange }, fieldState }) => (
                                            <div>
                                                <TextField.Textarea
                                                    style={{ color: Colors.LightGray }}
                                                    placeholder={''}
                                                    value={value}
                                                    onChange={onChange}
                                                    labeltext={'Issue Details (Please describe the problem)'}
                                                    className={'outline-none h-10 rounded'}
                                                />
                                                {fieldState?.error && (
                                                    <p style={{ color: Colors.Error }}>
                                                        {fieldState.error.message}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    />
                                </Col>
                            </Row>

                            <Row gutter={[16, 32]} className='my-6'>
                                <Col span={24} className='flex items-center justify-end'>
                                    <Button.Basic
                                        onClick={handleSubmit(handleSave)}
                                        htmlType='submit'
                                        loader={isSubmitting}
                                        disabled={
                                            !fieldValues.propertyReportingThisIssue ||
                                            !fieldValues.issueType ||
                                            (isPrefillMode && !fieldValues.incidentType)
                                        }
                                        size={'large'}
                                        text={isPrefillMode ? 'Assign Incident' : 'Create Incident'}
                                        buttonStyle={{ backgroundColor: colorPrimary, color: 'white' }}
                                        className={'px-12 rounded-md h-12'}
                                    />
                                </Col>
                            </Row>
                        </form>
                    </>
                }
            />
        </>
    )
}

export default AssignIncidentDrawer